import React, { useMemo, useState } from "react";

/**
 * AdminFlights.jsx
 * Airport-board style admin panel for managing Arrivals/Departures.
 * - Responsive: table on desktop, cards on mobile.
 * - Accessible: focus styles, aria labels, keyboard-friendly modal.
 */

const STATUSES = ["ON TIME", "BOARDING", "GATE OPEN", "LAST CALL", "DELAYED", "CANCELLED"];
const TERMINALS = ["T1", "T2", "T3", "T4"];

function pad2(n) {
  const s = String(n ?? "");
  return s.length === 1 ? `0${s}` : s;
}

function clampStr(s, max = 80) {
  const v = (s || "").trim();
  if (v.length <= max) return v;
  return v.slice(0, max - 1) + "…";
}

function normalizeTime(v) {
  // Accept: "8:5", "08:05", "805", "0805"
  const raw = String(v || "").trim();
  if (!raw) return "";
  if (/^\d{3,4}$/.test(raw)) {
    const hh = raw.length === 3 ? raw.slice(0, 1) : raw.slice(0, 2);
    const mm = raw.slice(-2);
    return `${pad2(hh)}:${pad2(mm)}`;
  }
  const m = raw.match(/^(\d{1,2})\s*:\s*(\d{1,2})$/);
  if (m) return `${pad2(m[1])}:${pad2(m[2])}`;
  return raw.slice(0, 5);
}

function uid() {
  // Works on all browsers (Chrome / Firefox / Safari / Edge / iOS / Android)
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  // Fallback (no eslint issues)
  return (
    Date.now().toString(36) +
    "-" +
    Math.random().toString(36).substring(2, 10)
  );
}


function useReducedMotion() {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    const onChange = () => setReduced(!!mq.matches);
    onChange();
    // Safari < 14 uses addListener
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener?.(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener?.(onChange);
    };
  }, []);
  return reduced;
}

function Modal({ open, title, children, onClose }) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  React.useEffect(() => {
    if (!open) return;
    const prev = document.activeElement;
    // focus first focusable
    const t = setTimeout(() => {
      const el = document.querySelector("[data-modal-root] button, [data-modal-root] input, [data-modal-root] select, [data-modal-root] textarea");
      el?.focus?.();
    }, 0);
    return () => {
      clearTimeout(t);
      prev?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-label="Close modal"
      />
      <div
        data-modal-root
        className="relative w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0B0F19] text-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div className="text-lg font-semibold">{title}</div>
          <button
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm text-white/80 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            ✕
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Badge({ value }) {
  const v = String(value || "").toUpperCase();
  const cls =
    v === "ON TIME"
      ? "bg-emerald-500/15 text-emerald-200 border-emerald-400/30"
      : v === "DELAYED"
      ? "bg-amber-500/15 text-amber-200 border-amber-400/30"
      : v === "CANCELLED"
      ? "bg-rose-500/15 text-rose-200 border-rose-400/30"
      : "bg-sky-500/15 text-sky-200 border-sky-400/30";
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold tracking-wide ${cls}`}>
      {v || "—"}
    </span>
  );
}

function ReadMore({ text, maxChars = 18 }) {
  const [open, setOpen] = useState(false);
  const safe = String(text || "");
  const needs = safe.length > maxChars;
  if (!needs) return <span className="whitespace-nowrap">{safe}</span>;

  return (
    <span className="inline-flex items-center gap-2">
      <span className="whitespace-nowrap">{open ? safe : clampStr(safe, maxChars)}</span>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="text-xs font-semibold text-amber-300 hover:text-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-400 rounded"
        aria-label={open ? "Show less" : "Read more"}
      >
        {open ? "Less" : "More"}
      </button>
    </span>
  );
}

function BoardShell({ title, terminal, children, icon = "✈️" }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-gradient-to-b from-[#0B0F19] to-[#070A12] shadow-[0_25px_70px_-35px_rgba(0,0,0,0.9)] overflow-hidden">
      <div className="relative">
        <div className="h-14 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-600">
          <div className="h-full px-5 flex items-center justify-between">
            <div className="flex items-center gap-3 text-[#1b1300] font-extrabold tracking-wide">
              <span className="text-lg">{icon}</span>
              <span className="text-lg">{title}</span>
            </div>
            <div className="text-[#1b1300] font-extrabold tracking-wide">
              {terminal}
            </div>
          </div>
        </div>
        <div className="absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-black/15 to-transparent pointer-events-none" />
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  );
}

function DesktopTable({ rows, kind, onSet, onModify }) {
  const timeLabel = kind === "departures" ? "Dep. Time" : "Arr. Time";

  return (
    <div className="hidden md:block">
      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-white/5">
            <tr className="text-xs uppercase tracking-wider text-white/70">
              <th className="px-4 py-3">{timeLabel}</th>
              <th className="px-4 py-3">City</th>
              <th className="px-4 py-3">Flight</th>
              <th className="px-4 py-3">Gate</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-[#070A12]">
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-white/10 hover:bg-white/5">
                <td className="px-4 py-3 font-mono text-amber-200 tracking-widest">{r.time || "—"}</td>
                <td className="px-4 py-3">
                  <span className="text-amber-100 font-semibold tracking-wide">
                    {String(r.city || "").toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-white/85">{r.flight || "—"}</td>
                <td className="px-4 py-3 font-mono text-white/85">{r.gate || "—"}</td>
                <td className="px-4 py-3"><Badge value={r.status} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onSet(r)}
                      className="rounded-lg bg-amber-400/15 border border-amber-400/30 px-3 py-2 text-xs font-semibold text-amber-200 hover:bg-amber-400/20 focus:outline-none focus:ring-2 focus:ring-amber-400"
                      aria-label={`Set ${r.city} ${r.flight}`}
                    >
                      Set
                    </button>
                    <button
                      onClick={() => onModify(r)}
                      className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-xs font-semibold text-white/90 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-amber-400"
                      aria-label={`Modify ${r.city} ${r.flight}`}
                    >
                      Modify
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-white/60">
                  No rows.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MobileCards({ rows, kind, onSet, onModify }) {
  const timeLabel = kind === "departures" ? "Dep. Time" : "Arr. Time";

  return (
    <div className="md:hidden space-y-3">
      {rows.map((r) => (
        <div
          key={r.id}
          className="rounded-2xl border border-white/10 bg-white/5 p-4"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-white/60">
                {timeLabel}
              </div>
              <div className="mt-1 font-mono text-amber-200 tracking-widest text-lg">
                {r.time || "—"}
              </div>
            </div>

            <div className="shrink-0">
              <Badge value={r.status} />
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/60">City</div>
              <div className="mt-1 text-amber-100 font-semibold tracking-wide">
                <ReadMore text={String(r.city || "").toUpperCase()} maxChars={16} />
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/60">Flight</div>
              <div className="mt-1 font-mono text-white/90">{r.flight || "—"}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/60">Gate</div>
              <div className="mt-1 font-mono text-white/90">{r.gate || "—"}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/60">Terminal</div>
              <div className="mt-1 font-mono text-white/90">{r.terminal || "—"}</div>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => onSet(r)}
              className="flex-1 rounded-xl bg-amber-400/15 border border-amber-400/30 px-3 py-3 text-sm font-semibold text-amber-200 hover:bg-amber-400/20 focus:outline-none focus:ring-2 focus:ring-amber-400"
            >
              Set
            </button>
            <button
              onClick={() => onModify(r)}
              className="flex-1 rounded-xl bg-white/5 border border-white/10 px-3 py-3 text-sm font-semibold text-white/90 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-amber-400"
            >
              Modify
            </button>
          </div>
        </div>
      ))}
      {rows.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-center text-white/60">
          No rows.
        </div>
      )}
    </div>
  );
}

export default function AdminFlights() {
  const reducedMotion = useReducedMotion();

  // Demo data (replace with API later)
  const [departures, setDepartures] = useState([
    { id: uid(), time: "08:20", city: "Dubai", flight: "YZ681", gate: "D25", status: "LAST CALL", terminal: "T3" },
    { id: uid(), time: "08:30", city: "San Francisco", flight: "SY458", gate: "D19", status: "GATE OPEN", terminal: "T3" },
    { id: uid(), time: "08:45", city: "Singapore", flight: "KQ207", gate: "B11", status: "DELAYED", terminal: "T3" },
    { id: uid(), time: "08:50", city: "Tokyo", flight: "LP708", gate: "B17", status: "GATE OPEN", terminal: "T3" },
  ]);

  const [arrivals, setArrivals] = useState([
    { id: uid(), time: "07:10", city: "Barcelona", flight: "KZ257", gate: "A02", status: "DELAYED", terminal: "T2" },
    { id: uid(), time: "08:45", city: "Bucarest", flight: "SO208", gate: "A09", status: "BOARDING", terminal: "T2" },
    { id: uid(), time: "08:50", city: "Paris", flight: "RF470", gate: "A11", status: "BOARDING", terminal: "T2" },
    { id: uid(), time: "08:80", city: "Dublin", flight: "YK815", gate: "A07", status: "ON TIME", terminal: "T2" },
  ]);

  const [activeTab, setActiveTab] = useState("departures"); // mobile convenience

  const [editing, setEditing] = useState(null); // row object
  const [editKind, setEditKind] = useState("departures"); // which list
  const [editDraft, setEditDraft] = useState(null);

  const totals = useMemo(
    () => ({
      dep: departures.length,
      arr: arrivals.length,
      delayed: [...departures, ...arrivals].filter((x) => String(x.status).toUpperCase() === "DELAYED").length,
    }),
    [departures, arrivals]
  );

  const openModify = (row, kind) => {
    setEditKind(kind);
    setEditing(row);
    setEditDraft({
      ...row,
      time: normalizeTime(row.time),
      city: row.city || "",
      flight: row.flight || "",
      gate: row.gate || "",
      terminal: row.terminal || "T1",
      status: (row.status || "ON TIME").toUpperCase(),
    });
  };

  const applyModify = () => {
    if (!editDraft) return;

    const next = {
      ...editDraft,
      time: normalizeTime(editDraft.time),
      city: (editDraft.city || "").trim(),
      flight: (editDraft.flight || "").trim().toUpperCase(),
      gate: (editDraft.gate || "").trim().toUpperCase(),
      terminal: (editDraft.terminal || "").trim().toUpperCase(),
      status: (editDraft.status || "").trim().toUpperCase(),
    };

    const update = (listSetter) => {
      listSetter((prev) => prev.map((x) => (x.id === next.id ? next : x)));
    };

    if (editKind === "departures") update(setDepartures);
    else update(setArrivals);

    setEditing(null);
    setEditDraft(null);
  };

  const quickSet = (row, kind) => {
    // Example “Set” action: cycle status in a predictable way
    const order = ["ON TIME", "GATE OPEN", "BOARDING", "LAST CALL", "DELAYED", "CANCELLED"];
    const cur = String(row.status || "ON TIME").toUpperCase();
    const idx = Math.max(0, order.indexOf(cur));
    const nextStatus = order[(idx + 1) % order.length];

    const update = (listSetter) => {
      listSetter((prev) =>
        prev.map((x) =>
          x.id === row.id ? { ...x, status: nextStatus } : x
        )
      );
    };

    if (kind === "departures") update(setDepartures);
    else update(setArrivals);
  };

  const addRow = (kind) => {
    const row = {
      id: uid(),
      time: "09:00",
      city: "New City Name",
      flight: "XX000",
      gate: "A01",
      status: "ON TIME",
      terminal: kind === "departures" ? "T3" : "T2",
    };
    if (kind === "departures") setDepartures((p) => [row, ...p]);
    else setArrivals((p) => [row, ...p]);
    openModify(row, kind);
  };

  return (
    <div className="min-h-screen bg-[#05070D] text-white">
      {/* Subtle background pattern */}
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.16]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 10%, rgba(255,193,7,0.18), transparent 40%), radial-gradient(circle at 80% 30%, rgba(56,189,248,0.16), transparent 45%), radial-gradient(circle at 50% 90%, rgba(34,197,94,0.10), transparent 55%)",
        }}
      />

      <div className="relative mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 sm:py-10">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              Admin Panel • Flight Boards
            </div>
            <h1 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight">
              Airport Board Admin
            </h1>
            <p className="mt-1 text-white/65 max-w-2xl">
              Manage departures and arrivals with a professional airport-style board UI.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
              <div className="text-[11px] uppercase tracking-wider text-white/60">Departures</div>
              <div className="mt-1 text-lg font-bold text-amber-200">{totals.dep}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
              <div className="text-[11px] uppercase tracking-wider text-white/60">Arrivals</div>
              <div className="mt-1 text-lg font-bold text-amber-200">{totals.arr}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
              <div className="text-[11px] uppercase tracking-wider text-white/60">Delayed</div>
              <div className="mt-1 text-lg font-bold text-amber-200">{totals.delayed}</div>
            </div>
          </div>
        </div>

        {/* Mobile tabs */}
        <div className="mt-6 md:hidden">
          <div className="flex rounded-2xl border border-white/10 bg-white/5 p-1">
            {["departures", "arrivals"].map((k) => {
              const active = activeTab === k;
              return (
                <button
                  key={k}
                  onClick={() => setActiveTab(k)}
                  className={
                    "flex-1 rounded-xl px-4 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-400 " +
                    (active ? "bg-amber-400/20 text-amber-200" : "text-white/75 hover:bg-white/10")
                  }
                >
                  {k === "departures" ? "Departures" : "Arrivals"}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6 grid gap-6">
          {/* Departures */}
          <div className={activeTab !== "departures" ? "md:block hidden" : ""}>
            <BoardShell title="Departures" terminal="Terminal 3" icon="🛫">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                <div className="text-white/70 text-sm">
                  Tip: “Set” cycles the status quickly (useful for ops).
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => addRow("departures")}
                    className="rounded-xl bg-amber-400/15 border border-amber-400/30 px-4 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-400/20 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  >
                    + Add Departure
                  </button>
                </div>
              </div>

              <DesktopTable
                rows={departures}
                kind="departures"
                onSet={(r) => quickSet(r, "departures")}
                onModify={(r) => openModify(r, "departures")}
              />
              <MobileCards
                rows={departures}
                kind="departures"
                onSet={(r) => quickSet(r, "departures")}
                onModify={(r) => openModify(r, "departures")}
              />
            </BoardShell>
          </div>

          {/* Arrivals */}
          <div className={activeTab !== "arrivals" ? "md:block hidden" : ""}>
            <BoardShell title="Arrivals" terminal="Terminal 2" icon="🛬">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                <div className="text-white/70 text-sm">
                  Keep city names short for best “board” readability.
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => addRow("arrivals")}
                    className="rounded-xl bg-amber-400/15 border border-amber-400/30 px-4 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-400/20 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  >
                    + Add Arrival
                  </button>
                </div>
              </div>

              <DesktopTable
                rows={arrivals}
                kind="arrivals"
                onSet={(r) => quickSet(r, "arrivals")}
                onModify={(r) => openModify(r, "arrivals")}
              />
              <MobileCards
                rows={arrivals}
                kind="arrivals"
                onSet={(r) => quickSet(r, "arrivals")}
                onModify={(r) => openModify(r, "arrivals")}
              />
            </BoardShell>
          </div>
        </div>
      </div>

      <Modal
        open={!!editing}
        title={editing ? `Modify: ${String(editing.city || "—").toUpperCase()} (${editing.flight || "—"})` : "Modify"}
        onClose={() => {
          setEditing(null);
          setEditDraft(null);
        }}
      >
        {!editDraft ? null : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <div className="text-xs uppercase tracking-wider text-white/60">Time (HH:MM)</div>
                <input
                  value={editDraft.time}
                  onChange={(e) => setEditDraft((p) => ({ ...p, time: e.target.value }))}
                  className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 font-mono text-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  inputMode="numeric"
                  placeholder="08:30"
                  aria-label="Time"
                />
              </label>

              <label className="block">
                <div className="text-xs uppercase tracking-wider text-white/60">City</div>
                <input
                  value={editDraft.city}
                  onChange={(e) => setEditDraft((p) => ({ ...p, city: e.target.value }))}
                  className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="Rome"
                  aria-label="City"
                />
              </label>

              <label className="block">
                <div className="text-xs uppercase tracking-wider text-white/60">Flight</div>
                <input
                  value={editDraft.flight}
                  onChange={(e) => setEditDraft((p) => ({ ...p, flight: e.target.value }))}
                  className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 font-mono text-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="AZ123"
                  aria-label="Flight"
                />
              </label>

              <label className="block">
                <div className="text-xs uppercase tracking-wider text-white/60">Gate</div>
                <input
                  value={editDraft.gate}
                  onChange={(e) => setEditDraft((p) => ({ ...p, gate: e.target.value }))}
                  className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 font-mono text-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="D25"
                  aria-label="Gate"
                />
              </label>

              <label className="block">
                <div className="text-xs uppercase tracking-wider text-white/60">Terminal</div>
                <select
                  value={editDraft.terminal}
                  onChange={(e) => setEditDraft((p) => ({ ...p, terminal: e.target.value }))}
                  className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                  aria-label="Terminal"
                >
                  {TERMINALS.map((t) => (
                    <option key={t} value={t} className="bg-[#0B0F19]">
                      {t}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <div className="text-xs uppercase tracking-wider text-white/60">Status</div>
                <select
                  value={editDraft.status}
                  onChange={(e) => setEditDraft((p) => ({ ...p, status: e.target.value }))}
                  className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                  aria-label="Status"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s} className="bg-[#0B0F19]">
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 pt-2">
              <button
                onClick={() => {
                  setEditing(null);
                  setEditDraft(null);
                }}
                className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white/85 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                Cancel
              </button>
              <button
                onClick={applyModify}
                className={
                  "rounded-xl px-4 py-2.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-400 " +
                  (reducedMotion ? "" : "transition")
                }
                style={{
                  background:
                    "linear-gradient(90deg, rgba(251,191,36,0.22), rgba(245,158,11,0.22))",
                  border: "1px solid rgba(251,191,36,0.35)",
                  color: "rgba(253,230,138,0.95)",
                }}
              >
                Save changes
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
